import { Apple, CalendarCheck, FlaskConical, Pill, type LucideIcon } from "lucide-react";
import type { ActionKind, CheckInChannel, StaffTitle } from "@ferrivia/api-contract";
import { useFormatters, useTranslation } from "@ferrivia/i18n";

export const ACTION_KINDS: ActionKind[] = ["supplement", "diet", "lab", "visit"];
export const CHECK_IN_CHANNELS: CheckInChannel[] = ["chat", "video_visit", "lab_retest"];

export const KIND_ICON: Record<ActionKind, LucideIcon> = {
  supplement: Pill,
  diet: Apple,
  lab: FlaskConical,
  visit: CalendarCheck,
};

export function useActionLabels() {
  const { t } = useTranslation("clinic");
  const { tag } = useFormatters();

  function kindLabel(kind: ActionKind): string {
    switch (kind) {
      case "supplement":
        return t("actions.kind.supplement", "Supplement");
      case "diet":
        return t("actions.kind.diet", "Diet");
      case "lab":
        return t("actions.kind.lab", "Lab");
      case "visit":
        return t("actions.kind.visit", "Visit");
    }
  }

  function channelLabel(channel: CheckInChannel): string {
    switch (channel) {
      case "chat":
        return t("actions.channel.chat", "Chat");
      case "video_visit":
        return t("actions.channel.videoVisit", "Video visit");
      case "lab_retest":
        return t("actions.channel.labRetest", "After lab re-test");
    }
  }

  function titleLabel(title: StaffTitle): string {
    switch (title) {
      case "physician":
        return t("actions.title.physician", "physician");
      case "nutrition_coach":
        return t("actions.title.nutritionCoach", "nutrition coach");
    }
  }

  function formatDay(isoDate: string): string {
    return new Intl.DateTimeFormat(tag, { day: "numeric", month: "short", timeZone: "UTC" }).format(
      new Date(`${isoDate}T00:00:00Z`),
    );
  }

  function formatTimestampDay(iso: string): string {
    return new Intl.DateTimeFormat(tag, { day: "numeric", month: "short" }).format(new Date(iso));
  }

  function runsLabel(weeks: number | null): string {
    return weeks === null
      ? t("actions.ongoing", "Ongoing")
      : t("actions.weeks", { count: weeks, defaultValue: "{{count}} weeks" });
  }

  return { kindLabel, channelLabel, titleLabel, formatDay, formatTimestampDay, runsLabel };
}
